import type { Element } from '@/types';
import { Project } from './types';
import {
  getAllProjects,
  saveProjects,
  getCurrentProjectId,
  setCurrentProjectId,
  generateProjectId,
} from './storage';
import helloProject from '@/hello.json';

// Generate unique project name
const generateProjectName = (): string => {
  const projects = getAllProjects();
  const projectNumbers = projects
    .map(p => p.name)
    .filter(name => name.match(/^Project \d+$/))
    .map(name => parseInt(name.replace('Project ', ''), 10))
    .filter(num => !isNaN(num));

  const maxNumber = projectNumbers.length > 0 ? Math.max(...projectNumbers) : 0;
  return `Project ${maxNumber + 1}`;
};

// Create a new project
export const createNewProject = (name?: string): Project => {
  const projectId = generateProjectId();
  const projectName = name || generateProjectName();

  const newProject: Project = {
    id: projectId,
    name: projectName,
    elements: [],
    viewport: {
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    },
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  const projects = getAllProjects();
  projects.push(newProject);
  saveProjects(projects);
  setCurrentProjectId(projectId);

  return newProject;
};

// Update a specific project
export const updateProject = (
  projectId: string,
  elements: Element[],
  viewport: { zoom: number; offsetX: number; offsetY: number },
  name?: string
): void => {
  const projects = getAllProjects();
  const projectIndex = projects.findIndex(p => p.id === projectId);

  if (projectIndex !== -1 && projects[projectIndex]) {
    projects[projectIndex].elements = elements;
    projects[projectIndex].viewport = viewport;
    projects[projectIndex].updatedAt = Date.now();

    if (name) {
      projects[projectIndex].name = name;
    }

    saveProjects(projects);
  }
};

// Save current project
export const saveCurrentProject = (
  elements: Element[],
  viewport: { zoom: number; offsetX: number; offsetY: number },
  name?: string
): void => {
  const currentProjectId = getCurrentProjectId();
  if (!currentProjectId) {
    // If no current project, create a new one
    const newProject = createNewProject(name);
    updateProject(newProject.id, elements, viewport, name);
    return;
  }

  updateProject(currentProjectId, elements, viewport, name);
};

// Load a project
export const loadProject = (projectId: string): Project | null => {
  const projects = getAllProjects();
  const project = projects.find(p => p.id === projectId);

  if (project) {
    setCurrentProjectId(projectId);
    return project;
  }

  return null;
};

// Get current project
export const getCurrentProject = (): Project | null => {
  const currentProjectId = getCurrentProjectId();
  if (!currentProjectId) return null;

  return loadProject(currentProjectId);
};

// Delete a project
export const deleteProject = (projectId: string): void => {
  console.log('deleteProject called with ID:', projectId);
  const projects = getAllProjects();
  console.log('All projects before filtering:', projects);

  const filteredProjects = projects.filter(p => p.id !== projectId);
  console.log('Filtered projects:', filteredProjects);

  saveProjects(filteredProjects);
  console.log('Projects saved to localStorage');

  // If we deleted the current project, clear the current project ID
  const currentProjectId = getCurrentProjectId();
  if (currentProjectId === projectId) {
    console.log('Deleted project was current, clearing current project ID');
    try {
      localStorage.removeItem('canvasDraw_currentProjectId');
    } catch (error) {
      console.warn('Failed to clear current project ID:', error);
    }
  }
};

// Rename a project
export const renameProject = (projectId: string, newName: string): void => {
  const projects = getAllProjects();
  const project = projects.find(p => p.id === projectId);

  if (project) {
    project.name = newName;
    project.updatedAt = Date.now();
    saveProjects(projects);
  }
};

// Initialize projects (create default if none exist)
export const initializeProjects = (): Project => {
  const projects = getAllProjects();
  const currentProjectId = getCurrentProjectId();

  // If there are projects and current project exists, load it
  if (projects.length > 0 && currentProjectId) {
    const currentProject = projects.find(p => p.id === currentProjectId);
    if (currentProject) {
      return currentProject;
    }
  }

  // If no projects exist, use hello.json as the starter project
  if (projects.length === 0) {
    const starterProject: Project = {
      ...(helloProject as Project),
      id: generateProjectId(),
      name: 'Welcome Project',
      elements: (helloProject as Project).elements.map(element => ({
        ...element,
        isSelected: false, // Ensure isSelected is set
      })),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    // Save the starter project to localStorage
    saveProjects([starterProject]);
    setCurrentProjectId(starterProject.id);

    return starterProject;
  }

  // Otherwise create a new project
  return createNewProject();
};
